import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, Redirect } from "react-router-dom";
import { Box, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";
import moment from "moment";
import Navbar from "components/Navbar";
import DiagnosticsBar from "components/Masterbar/DiagnosticsBar";
import DarkLayout from "components/Shared/DarkLayout";
import {
  getMobileLog,
  selectDiagnosticsLoading,
  selectSelectedMobileLog,
  setSelectedMobileLog,
} from "redux/slices/diagnosticsSlice";
import { selectUser } from "redux/slices/userSlice";
import { PATHS } from "util/appConstants";

const useStyles = makeStyles({
  _heading: {
    color: "#F5F5F5",
    font: "normal normal normal 28px/40px Questrial",
  },
  _edit: {
    color: "#6F9CEB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.3)",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  _buttonbox: {
    display: "flex",
    margin: "0 16px",
  },
  _edittext: {
    width: "24px",
    height: "16px",
    color: "#6F9CEB",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
    display: "none",
    position: "absolute",
    marginLeft: "20px",
    transition: "all 0.3s ease-in-out",
  },
  _infoheading: {
    color: "#F5F5F5",
    font: "normal normal medium 22px/32px Roboto",
    marginTop: "44px",
  },
  _basicdetailhead: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: "41px",
  },
  _field: {
    marginRight: "48px",
    marginBottom: "16px",
  },
  _head: {
    color: "#F5F5F5",
    opacity: "0.6",
    font: "normal normal normal 12px/24px Roboto",
  },
  _detail: {
    color: "#F5F5F5",
    font: "normal normal normal 18px/24px Roboto",
  },
  meta: {
    color: "#ADADAD",
    fontSize: 13,
    whiteSpace: "pre-wrap",
    maxHeight: 480,
    overflow: "auto",
    background: "#1F1F1F",
    padding: 12,
    borderRadius: 4,
    marginTop: 8,
  },
});

const isDiagnosticsVisible = (user) =>
  user?.isSuperAdmin && import.meta.env.VITE_SHOW_SUPPORT === "true";

const formatPayload = (payload) => {
  if (!payload) {
    return "-";
  }
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch (error) {
    return payload;
  }
};

const MobileLogDetail = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const user = useSelector(selectUser);
  const loading = useSelector(selectDiagnosticsLoading);
  const log = useSelector(selectSelectedMobileLog);

  useEffect(() => {
    if (!isDiagnosticsVisible(user) || !id) {
      return;
    }
    dispatch(getMobileLog(id));
    return () => {
      dispatch(setSelectedMobileLog(null));
    };
  }, [dispatch, user, id]);

  const payloadText = useMemo(() => formatPayload(log?.payload), [log?.payload]);

  if (!isDiagnosticsVisible(user)) {
    return <Redirect to={PATHS.admin.supplierGroups} />;
  }

  return (
    <>
      <Navbar />
      <DiagnosticsBar />
      <DarkLayout doublebar loading={loading || !log}>
        <div className={classes._editbox}>
          <Typography className={classes._heading} variant="h4">
            {t("Mobile Log")} #{log?.id}
          </Typography>
          <Box className={classes._buttonbox} component="div">
            <CloseIcon
              onClick={() =>
                history.push(
                  log?.route_id
                    ? PATHS.diagnostics.mobileRouteLogs.replace(":routeId", log.route_id)
                    : PATHS.diagnostics.mobile,
                )
              }
              className={classes._edit}
            />
            <Typography component="span" className={clsx(classes._edittext, "edittag")}>
              {t("Close")}
            </Typography>
          </Box>
        </div>

        <div className={classes._basicdetailhead}>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Route ID")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {log?.route_id ?? "-"}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Event")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {log?.event_type || "-"}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Level")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {log?.level || "-"}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Client Time")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {log?.client_timestamp
                ? moment(log.client_timestamp).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Created")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {log?.created_at
                ? moment(log.created_at).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Typography>
          </div>
        </div>

        <Typography className={classes._infoheading} variant="h5">
          {t("Payload")}
        </Typography>
        <Typography className={classes.meta} component="pre">
          {payloadText}
        </Typography>
      </DarkLayout>
    </>
  );
};

export default MobileLogDetail;
